import {lazy, Suspense, useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import {useFoundationTheme} from '../../hooks/useFoundationTheme';
import {Integer} from '../../types/Integer';
import {ThemeLottieKey} from '../../utils/foundationTheme';
import {getThemeValue} from '../../utils/getThemeValue';
import {Box} from '../Box/Box';

// The dotlottie player (its JS runtime and ~1.7 MB wasm) is loaded lazily so it
// is code-split into an async chunk instead of every consumer's initial bundle.
const DotLottiePlayer = lazy(() =>
  import('./DotLottiePlayer').then((module) => ({default: module.DotLottiePlayer}))
);

export interface LottieProps {
  value: ThemeLottieKey;
  minHeight: Integer;
  autoplay?: boolean;
  loop?: boolean;
}

type LottieSourceState = {
  value: ThemeLottieKey;
  lotties: Readonly<Partial<Record<string, () => Promise<string>>>>;
  loader: Promise<string> | null;
};

// Cache for loaded Lottie sources with size limit to prevent memory leaks
const lottieSourceCache = new Map<string, Promise<string>>();
const MAX_CACHE_SIZE = 50;

// Simple cache cleanup function - removes oldest entries when limit is exceeded
function cleanupCache() {
  if (lottieSourceCache.size >= MAX_CACHE_SIZE) {
    // Remove the oldest entries (first entries in Map maintain insertion order)
    const entriesToRemove = lottieSourceCache.size - MAX_CACHE_SIZE + 1;
    const keys = Array.from(lottieSourceCache.keys()).slice(0, entriesToRemove);
    keys.forEach((key) => lottieSourceCache.delete(key));
  }
}

// Move cache entry to end by re-inserting (LRU behavior)
function moveToEnd(key: string): Promise<string> | null {
  const source = lottieSourceCache.get(key);
  if (isNil(source)) {
    return null;
  }

  lottieSourceCache.delete(key);
  lottieSourceCache.set(key, source);
  return source;
}

function getLottieSource(
  value: ThemeLottieKey,
  lotties: Readonly<Partial<Record<string, () => Promise<string>>>>
): Promise<string> | null {
  const cachedSource = moveToEnd(value);
  if (isNotNil(cachedSource)) {
    return cachedSource;
  }

  const loader = getThemeValue<() => Promise<string>>(lotties, value);
  if (isNil(loader)) {
    return null;
  }

  const loaderPromise = loader();

  cleanupCache();
  lottieSourceCache.set(value, loaderPromise);

  loaderPromise.catch(() => {
    lottieSourceCache.delete(value);
  });

  return loaderPromise;
}

export function Lottie(props: LottieProps) {
  const theme = useFoundationTheme();
  const {value, minHeight, ...rest} = props;
  const [sourceState, setSourceState] = useState<LottieSourceState>(() => ({
    value,
    lotties: theme.lotties,
    loader: getLottieSource(value, theme.lotties),
  }));
  const hasSourceChanged = sourceState.value !== value || sourceState.lotties !== theme.lotties;
  const loader = hasSourceChanged ? getLottieSource(value, theme.lotties) : sourceState.loader;

  if (hasSourceChanged) {
    setSourceState({value, lotties: theme.lotties, loader});
  }

  if (isNil(loader)) {
    return null;
  }

  return (
    <Suspense fallback={<Box minHeight={minHeight} />}>
      <DotLottiePlayer loader={loader} {...rest} />
    </Suspense>
  );
}
